import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { MapPin, Filter, Loader2, Plus, Tag, Info } from 'lucide-react';

import MapView from './MapView';
import FilterPanel from './FilterPanel';
import CreateCampaignModal from './CreateCampaignModal';
import { DEFAULT_FILTERS, type FilterState } from '../constants';
import { queryProperties } from '../services/properties';
import type { ProspectQueryResult } from '../types';
import { lookupPostcodesBulk, pointsToBoundingPolygon, approxPolygonAreaKm2 } from '../services/geo';

interface ProspectScreenProps {
  user: User;
  onCampaignCreated: (campaignId: string) => void;
  onUpgradeRequested: () => void;
}

export default function ProspectScreen({ user, onCampaignCreated, onUpgradeRequested }: ProspectScreenProps) {
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [polygon, setPolygon] = useState<GeoJSON.Polygon | null>(null);
  const [postcodeInput, setPostcodeInput] = useState('');
  const [postcodes, setPostcodes] = useState<string[]>([]);
  const [drawing, setDrawing] = useState(false);
  const [result, setResult] = useState<ProspectQueryResult>({ total: 0, sample: [] });
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced query whenever filters / polygon / postcodes change.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await queryProperties({ polygon, postcodes, filters });
        setResult(r);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [filters, polygon, postcodes]);

  const areaKm2 = useMemo(() => (polygon ? approxPolygonAreaKm2(polygon) : 0), [polygon]);

  const addPostcodes = async () => {
    const tokens = postcodeInput
      .split(/[\s,;]+/)
      .map((p) => p.trim().toUpperCase())
      .filter(Boolean);
    if (!tokens.length) return;
    const next = Array.from(new Set([...postcodes, ...tokens]));
    setPostcodes(next);
    setPostcodeInput('');

    // If no polygon, derive a bounding box from the postcode centroids so the map
    // visually reflects the selection.
    if (!polygon) {
      const looked = await lookupPostcodesBulk(next);
      const bb = pointsToBoundingPolygon(looked, 0.01);
      if (bb) setPolygon(bb);
    }
  };

  const removePostcode = (p: string) => {
    setPostcodes(postcodes.filter((x) => x !== p));
  };

  const reset = () => {
    setFilters(DEFAULT_FILTERS);
    setPolygon(null);
    setPostcodes([]);
  };

  return (
    <div className="flex-1 min-h-0 flex">
      <aside className="w-80 shrink-0 border-r border-slate-200 bg-white flex flex-col">
        <div className="p-5 border-b border-slate-200">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <Filter size={16} /> Filters
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Narrow your territory by signal strength and property characteristics.
          </p>
        </div>

        <div className="p-5 border-b border-slate-200">
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Postcodes
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={postcodeInput}
              onChange={(e) => setPostcodeInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPostcodes())}
              placeholder="CM1 1AB, SS1 2CD…"
              className="flex-1 px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:border-emerald-400"
            />
            <button
              onClick={addPostcodes}
              className="px-2 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-md"
              aria-label="Add"
            >
              <Plus size={14} />
            </button>
          </div>
          {postcodes.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {postcodes.map((p) => (
                <button
                  key={p}
                  onClick={() => removePostcode(p)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded text-xs hover:bg-emerald-100"
                >
                  <Tag size={10} /> {p}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="p-5 flex-1 overflow-y-auto">
          <FilterPanel filters={filters} onChange={setFilters} />
        </div>

        <div className="p-5 border-t border-slate-200 bg-slate-50">
          <button
            onClick={reset}
            className="text-xs text-slate-500 hover:text-slate-700"
          >
            Reset all filters
          </button>
        </div>
      </aside>

      <div className="flex-1 min-h-0 flex flex-col relative">
        {result.is_demo && (
          <div className="absolute top-3 left-3 z-10 max-w-sm flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg shadow text-xs">
            <Info size={14} className="shrink-0 mt-0.5" />
            <span>
              <strong>Demo data.</strong> The properties table is empty — run the
              Land Registry ingestion edge function to see real data. You can
              still try the full create-campaign flow with these sample pins.
            </span>
          </div>
        )}
        <MapView
          pins={result.sample}
          polygon={polygon}
          onPolygonChange={setPolygon}
          drawing={drawing}
          onDrawingChange={setDrawing}
        />

        <div className="border-t border-slate-200 bg-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
                <MapPin size={12} /> Targets
                {loading && <Loader2 size={12} className="animate-spin" />}
              </div>
              <div className="text-2xl font-semibold text-slate-900">
                {result.total.toLocaleString()}
              </div>
            </div>
            {polygon && (
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500">Territory</div>
                <div className="text-sm font-medium text-slate-700">{areaKm2.toFixed(1)} km²</div>
              </div>
            )}
            {result.sample.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500">Avg score</div>
                <div className="text-sm font-medium text-slate-700">
                  {Math.round(
                    result.sample.reduce((acc, p) => acc + p.move_likelihood, 0) /
                      result.sample.length
                  )}
                </div>
              </div>
            )}
          </div>

          <button
            disabled={result.total < 1}
            onClick={() => setShowCreate(true)}
            className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg shadow flex items-center gap-2 transition-colors"
          >
            <Plus size={16} /> Create campaign
          </button>
        </div>

        {showCreate && (
          <CreateCampaignModal
            user={user}
            filters={filters}
            polygon={polygon}
            postcodes={postcodes}
            targetCount={result.total}
            isDemo={!!result.is_demo}
            onClose={() => setShowCreate(false)}
            onCreated={(id) => {
              setShowCreate(false);
              onCampaignCreated(id);
            }}
            onUpgradeRequested={() => {
              setShowCreate(false);
              onUpgradeRequested();
            }}
          />
        )}
      </div>
    </div>
  );
}
